import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle, GeoJSON, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { useAuth } from '../../contexts/AuthContext';
import api, { getBackendUrl } from '../../config/api';
import { parseSignalkPosition, parseSignalkNavigation, parseSignalkHeading, parseSignalkSpeed, parseSignalkDepth } from '../../utils/signalKValueFormat';
import {
  STREET_MAP_URL,
  STREET_MAP_ATTRIBUTION,
  STREET_MAP_SUBDOMAINS,
  SATELLITE_MAP_URL,
  SATELLITE_MAP_ATTRIBUTION,
  VesselMapTracker,
  MapZoomLimits,
  MapInvalidateSize,
  ChartViewportPersistence,
  loadChartViewport,
  CHART_MIN_ZOOM,
  CHART_MAX_ZOOM,
  ensureChartPrefsDefaults,
  IntegratedMarineChartLayer,
  MarineDepthOverlays,
  probeChartServices,
} from './marineMapLayers';
import {
  getMarineChartRegion,
  chartRegionLabel,
  chartRegionDescription,
} from './marineChartRegion';
import { useVesselPosition, CHART_ZOOM } from '../../hooks/useVesselPosition';
import { useTripRecorder } from '../../hooks/useTripRecorder';
import { useMob, MobMarker, MobPanel } from './MobControl';
import PlaceSearch from './PlaceSearch';
import MarinePoiLayer from './MarinePoiLayer';
import SearchPlaceMarker from './SearchPlaceMarker';
import WeatherAlertBanner from './WeatherAlertBanner';
import { navionicsWebChartUrl } from './cmapSetup';
import toast from 'react-hot-toast';
import {
  MapIcon,
  UserGroupIcon,
  PlusIcon,
  XMarkIcon,
  BellIcon,
  ArrowPathIcon,
  UserPlusIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  TrashIcon,
  Bars3Icon,
  PlayIcon,
  StopIcon,
} from '@heroicons/react/24/outline';

/** Compass / locate-on-boat icon (common chartplotter recenter affordance). */
function CompassRecenterIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="12" cy="12" r="2.25" fill="currentColor" stroke="none" />
      <path d="M12 2.5v2.25M12 19.25v2.25M2.5 12h2.25M19.25 12H21.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <path d="M12 5.5 10.6 9.2h2.8L12 5.5z" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  padding: 1rem;
`;

const Header = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes.h1};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${props => props.variant === 'primary' ? theme.colors.primary.main : theme.colors.background.secondary};
  color: ${props => props.variant === 'primary' ? 'white' : theme.colors.text.primary};
  border: 1px solid ${props => props.variant === 'primary' ? theme.colors.primary.main : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'primary' ? theme.colors.primary.dark : theme.colors.background.tertiary};
  }
`;

const MapWrapper = styled.div`
  width: calc(100% - 4rem);
  height: calc(100vh - 300px);
  max-height: 700px;
  min-height: 500px;
  margin: 2rem auto;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  border: 2px solid ${theme.colors.border.primary};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
  
  .boatwise-enc-chart-layer {
    image-rendering: auto;
  }
  
  .leaflet-container {
    height: 100%;
    width: 100%;
    background: #e8e4dc;
    cursor: grab !important;
    
    &.leaflet-dragging {
      cursor: grabbing !important;
    }
  }
  
  /* Enhance depth layer visibility with CSS filters */
  .leaflet-tile-pane img[src*="openseamap.org/depth"],
  .leaflet-tile-pane img[src*="openseamap.org/waterdepth"] {
    filter: contrast(1.5) brightness(1.2) saturate(1.3) !important;
    mix-blend-mode: screen !important;
  }
  
  /* CHS NONNA bathymetry WMS depth contours */
  .leaflet-tile-pane img[src*="chs-bathymetry-wms"],
  .leaflet-tile-pane img[src*="enc-wms"] {
    filter: contrast(1.2) brightness(1.05) !important;
  }
  
  .vessel-marker-pane {
    z-index: 700 !important;
  }
  
  .leaflet-pane,
  .leaflet-map-pane,
  .leaflet-tile-pane,
  .leaflet-overlay-pane {
    pointer-events: auto !important;
  }
  
  .leaflet-interactive {
    cursor: pointer;
  }
`;

const DepthHud = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 1100;
  padding: 0.5rem 0.85rem;
  background: rgba(15, 23, 42, 0.88);
  color: #e2e8f0;
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  pointer-events: none;

  span.label {
    display: block;
    font-size: 0.65rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #94a3b8;
    margin-bottom: 0.15rem;
  }

  span.value {
    font-size: 1.25rem;
    font-variant-numeric: tabular-nums;
    color: ${(props) => {
      if (props.$missing) return '#94a3b8';
      return props.$shallow ? '#fbbf24' : '#7dd3fc';
    }};
  }
`;

const DepthHudNote = styled.div`
  font-size: 0.62rem;
  font-weight: 500;
  color: #64748b;
  margin-top: 0.2rem;
  max-width: 11rem;
  line-height: 1.3;
`;

const ChartSourceHint = styled.div`
  position: absolute;
  bottom: 2.5rem;
  left: 1rem;
  z-index: 1050;
  max-width: 18rem;
  padding: 0.4rem 0.65rem;
  background: rgba(255, 255, 255, 0.92);
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.7rem;
  line-height: 1.35;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  pointer-events: none;
`;

const RecordingBadge = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(220, 38, 38, 0.92);
  color: white;
  border-radius: ${theme.borderRadius.lg};
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);

  span.dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: white;
    animation: pulse 1.5s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
  }
`;

const GpsHint = styled.div`
  position: absolute;
  bottom: 5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  padding: 0.625rem 1rem;
  background: rgba(15, 23, 42, 0.88);
  color: ${theme.colors.text.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  font-size: 0.8125rem;
  pointer-events: none;
`;

const Sidebar = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 300px;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1rem;
  z-index: 1000;
  max-height: calc(100vh - 250px);
  overflow-y: auto;
`;

const SidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.border.secondary};
`;

const SidebarTitle = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
`;

const FriendListItem = styled.div`
  padding: 0.75rem;
  border-radius: ${theme.borderRadius.md};
  background: ${props => props.$active ? theme.colors.primary.light : theme.colors.background.secondary};
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.background.tertiary};
  }
`;

const FriendName = styled.div`
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.25rem;
`;

const FriendInfo = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.medium};
  background: ${props => props.$active ? theme.colors.success.light : theme.colors.background.tertiary};
  color: ${props => props.$active ? theme.colors.success.main : theme.colors.text.secondary};
  margin-top: 0.25rem;
`;

const FriendsPanel = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 400px;
  max-height: calc(100vh - 200px);
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1rem;
  z-index: 1001;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  margin-bottom: 1rem;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
  }
`;

const TabButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  background: ${props => props.$active ? theme.colors.primary.main : 'transparent'};
  color: ${props => props.$active ? 'white' : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  margin-right: 0.5rem;

  &:hover {
    background: ${props => props.$active ? theme.colors.primary.dark : theme.colors.background.tertiary};
  }
`;

const ActionButton = styled.button`
  padding: 0.25rem 0.5rem;
  border: none;
  background: ${props => props.variant === 'success' ? theme.colors.success.main : props.variant === 'danger' ? theme.colors.error.main : theme.colors.primary.main};
  color: white;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  font-size: ${theme.fontSizes.xs};
  margin-left: 0.5rem;

  &:hover {
    opacity: 0.9;
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${theme.colors.primary.main};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${theme.fontWeights.semibold};
  font-size: ${theme.fontSizes.sm};
  margin-right: 0.75rem;
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: ${theme.colors.error.main};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.md};
  z-index: 1000;
  display: flex;
  align-items: center;
`;

const MapMenuButton = styled.button`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  z-index: 1002;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  padding: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.background.tertiary};
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
  }
`;

/** Floating recenter — stays on the map (chartplotter industry pattern). */
const MapRecenterButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 1002;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    background: #f0f9ff;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.24);
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MapMenu = styled.div`
  position: absolute;
  bottom: 4rem;
  left: 1rem;
  z-index: 1002;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1rem;
  min-width: 250px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${theme.colors.border.secondary};

  &:last-child {
    border-bottom: none;
  }
`;

const MenuLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  flex: 1;
`;

const MenuCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

// Leaflet replaces the marker's DOM node whenever it receives a new icon
// object, so icons must be cached — recreating them on every render made
// every marker flicker on each GPS update.
const vesselIconCache = new Map();
const createVesselIcon = (color = '#3b82f6', heading = 0) => {
  const rounded = Math.round((heading || 0) / 2) * 2; // 2° steps keep cache small
  const key = `${color}|${rounded}`;
  if (!vesselIconCache.has(key)) {
    vesselIconCache.set(key, L.divIcon({
      className: 'custom-vessel-icon',
      html: `
        <div style="
          transform: rotate(${rounded}deg);
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-bottom: 20px solid ${color};
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
        "></div>
      `,
      iconSize: [20, 20],
      iconAnchor: [10, 20],
      popupAnchor: [0, -20],
    }));
  }
  return vesselIconCache.get(key);
};

// AIS vessel icon
const aisIconCache = new Map();
const createAisVesselIcon = (color = '#ef4444') => {
  if (!aisIconCache.has(color)) {
    aisIconCache.set(color, L.divIcon({
      className: 'ais-vessel-marker',
      html: `<div style="
        width: 12px;
        height: 12px;
        background-color: ${color};
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      "></div>`,
      iconSize: [12, 12],
      iconAnchor: [6, 6]
    }));
  }
  return aisIconCache.get(color);
};

// Friend vessel icon
const friendIconCache = new Map();
const createFriendIcon = (color = '#10b981') => {
  if (!friendIconCache.has(color)) {
    friendIconCache.set(color, L.divIcon({
      className: 'custom-friend-icon',
      html: `
        <div style="
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background-color: ${color};
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        "></div>
      `,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12],
    }));
  }
  return friendIconCache.get(color);
};

// Component to ensure dragging is enabled
function MapDraggingEnabler() {
  const map = useMap();
  
  useEffect(() => {
    // Force enable dragging and all interactions
    const enableAll = () => {
      try {
        // Check if dragging exists and is disabled
        if (map.dragging) {
          if (!map.dragging.enabled()) {
            map.dragging.enable();
            console.log('✅ Dragging enabled');
          } else {
            console.log('✅ Dragging already enabled');
          }
        } else {
          console.warn('⚠️ map.dragging is not available');
        }
        
        // Enable all other interactions
        if (map.touchZoom && !map.touchZoom.enabled()) {
          map.touchZoom.enable();
        }
        if (map.doubleClickZoom && !map.doubleClickZoom.enabled()) {
          map.doubleClickZoom.enable();
        }
        if (map.scrollWheelZoom && !map.scrollWheelZoom.enabled()) {
          map.scrollWheelZoom.enable();
        }
        if (map.boxZoom && !map.boxZoom.enabled()) {
          map.boxZoom.enable();
        }
        if (map.keyboard && !map.keyboard.enabled()) {
          map.keyboard.enable();
        }
        
        // Log map state for debugging
        console.log('Map dragging state:', {
          dragging: map.dragging?.enabled(),
          scrollWheelZoom: map.scrollWheelZoom?.enabled(),
          doubleClickZoom: map.doubleClickZoom?.enabled(),
        });
      } catch (error) {
        console.error('Error enabling map interactions:', error);
      }
    };
    
    // Enable immediately
    enableAll();
    
    // Also enable after delays to ensure map is fully initialized
    const timeout1 = setTimeout(enableAll, 100);
    const timeout2 = setTimeout(enableAll, 500);
    
    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
    };
  }, [map]);
  
  return null;
}

// Component to handle map clicks only when plotting course
function MapClickHandler({ onMapClick }) {
  const map = useMap();
  
  useEffect(() => {
    const handleClick = (e) => {
      onMapClick(e);
    };
    
    map.on('click', handleClick);
    
    return () => {
      map.off('click', handleClick);
    };
  }, [map, onMapClick]);
  
  return null;
}

// Component to track map bounds and fetch AIS vessels / POIs.
// The callback lives in a ref so this effect runs once per map — previously
// it re-ran on every parent render (each GPS update), refetching AIS/POIs
// nonstop and making the map feel like it was constantly refreshing.
function MapBoundsTracker({ onBoundsChange }) {
  const map = useMap();
  const callbackRef = useRef(onBoundsChange);
  callbackRef.current = onBoundsChange;

  useEffect(() => {
    let debounceTimer = null;
    let lastKey = null;

    const updateBounds = () => {
      if (map._boatwiseProgrammaticMove > 0) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (map._boatwiseProgrammaticMove > 0) return;
        const bounds = map.getBounds();
        // Skip if the view hasn't meaningfully moved (e.g. follow-mode nudges)
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const key = `${sw.lat.toFixed(2)},${sw.lng.toFixed(2)},${ne.lat.toFixed(2)},${ne.lng.toFixed(2)}`;
        if (key === lastKey) return;
        lastKey = key;
        callbackRef.current(bounds);
      }, 1200);
    };

    // Initial fetch (no need to wait for a debounce on mount)
    callbackRef.current(map.getBounds());

    map.on('moveend', updateBounds);
    map.on('zoomend', updateBounds);

    return () => {
      clearTimeout(debounceTimer);
      map.off('moveend', updateBounds);
      map.off('zoomend', updateBounds);
    };
  }, [map]);

  return null;
}

// Flies the map to a searched place when one is selected (once per target).
function SearchFlyTo({ target }) {
  const map = useMap();
  const lastFlownKeyRef = useRef('');

  useEffect(() => {
    if (!target) {
      lastFlownKeyRef.current = '';
      return;
    }
    const lat = typeof target.latitude === 'number' ? target.latitude : parseFloat(target.latitude);
    const lng = typeof target.longitude === 'number' ? target.longitude : parseFloat(target.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const key = `${lat.toFixed(6)},${lng.toFixed(6)},${target.name || ''}`;
    if (key === lastFlownKeyRef.current) return;
    lastFlownKeyRef.current = key;

    map.stop();
    map._boatwiseProgrammaticMove = (map._boatwiseProgrammaticMove || 0) + 1;
    map.flyTo([lat, lng], map.getZoom() > 0 ? map.getZoom() : 13, { duration: 0.8 });
    map.once('moveend', () => {
      map._boatwiseProgrammaticMove = Math.max(0, (map._boatwiseProgrammaticMove || 1) - 1);
    });
  }, [map, target]);

  return null;
}

const ChartPlotter = () => {
  ensureChartPrefsDefaults();
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    vesselId,
    vesselIdRef,
    location: myLocation,
    loading: locationLoading,
    source: locationSource,
    fetchPosition,
    updatePosition,
    setLocation: setMyLocation,
  } = useVesselPosition();
  const {
    activeTrip,
    trackPoints,
    recording,
    busy: tripBusy,
    startTrip,
    stopTrip,
  } = useTripRecorder(vesselId, myLocation);
  const [friends, setFriends] = useState([]);
  const [friendLocations, setFriendLocations] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [proximityAlerts, setProximityAlerts] = useState([]);
  const [isPlottingCourse, setIsPlottingCourse] = useState(false);
  const [courseWaypoints, setCourseWaypoints] = useState([]);
  const [locationPreferences, setLocationPreferences] = useState(null);
  const [showFriendsPanel, setShowFriendsPanel] = useState(false);
  const [friendRequests, setFriendRequests] = useState({ received: [], sent: [] });
  const [friendsTab, setFriendsTab] = useState('friends'); // 'friends' or 'requests'
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [aisVessels, setAisVessels] = useState([]);
  const [showAisVessels, setShowAisVessels] = useState(true);
  const [showFriendsOnMap, setShowFriendsOnMap] = useState(true);
  const [showMapMenu, setShowMapMenu] = useState(false);
  const [mapStyle, setMapStyle] = useState(
    () => localStorage.getItem('map_base_layer') || 'marine'
  );
  const [showChsBathymetry, setShowChsBathymetry] = useState(
    () => localStorage.getItem('show_chs_bathymetry') !== 'false'
  );
  const [showDepthLayers, setShowDepthLayers] = useState(
    () => localStorage.getItem('show_depth_layers') !== 'false'
  );
  const [chartServices, setChartServices] = useState(null);
  const [navionicsKey, setNavionicsKey] = useState(null);
  const [useVectorCharts, setUseVectorCharts] = useState(
    () => localStorage.getItem('use_vector_charts') === 'true'
  );
  const [followVessel, setFollowVessel] = useState(() => {
    const saved = localStorage.getItem('follow_vessel');
    return saved !== null ? saved === 'true' : true;
  });
  const [recenterToken, setRecenterToken] = useState(0);

  const handleRecenterVessel = useCallback(() => {
    setSearchTarget(null);
    setFollowVessel(true);
    localStorage.setItem('follow_vessel', 'true');
    setRecenterToken((n) => n + 1);
  }, []);

  const { mob, activateMob, clearMob } = useMob();
  const [searchTarget, setSearchTarget] = useState(null);
  const [showPois, setShowPois] = useState(() => {
    const saved = localStorage.getItem('show_marine_pois');
    return saved !== 'false'; // on by default — marinas/fuel/locks are core chart plotter data
  });
  const [pois, setPois] = useState([]);
  const [poiDetails, setPoiDetails] = useState({});
  const [poisTooWide, setPoisTooWide] = useState(false);
  const showPoisRef = useRef(showPois);
  showPoisRef.current = showPois;
  const lastBoundsRef = useRef(null);
  const wsRef = useRef(null);
  const locationUpdateIntervalRef = useRef(null);
  const positionPartsRef = useRef({ latitude: null, longitude: null, latAt: 0, lonAt: 0 });
  const lastRealHeadingAtRef = useRef(0);

  const handleStartTrip = async () => {
    if (!vesselId) {
      toast.error('No vessel selected');
      return;
    }
    try {
      await startTrip();
      toast.success('Recording track — GPS points save automatically');
    } catch (err) {
      console.error(err);
      toast.error('Could not start trip');
    }
  };

  const handleStopTrip = async () => {
    try {
      const completed = await stopTrip();
      if (completed) {
        toast.success('Trip saved');
        navigate(`/trips/${completed.id}`);
      }
    } catch (err) {
      console.error(err);
      toast.error('Could not save trip');
    }
  };

  const applyVesselPosition = (latitude, longitude, partial = false, navExtras = {}) => {
    if (partial) {
      const parts = positionPartsRef.current;
      const now = Date.now();
      if (Number.isFinite(latitude)) {
        parts.latitude = latitude;
        parts.latAt = now;
      }
      if (Number.isFinite(longitude)) {
        parts.longitude = longitude;
        parts.lonAt = now;
      }
      // Only move the marker once both halves of the fix are from the same
      // moment — pairing a new latitude with a stale longitude (or vice
      // versa) made the marker hop diagonally between updates.
      if (Math.abs((parts.latAt || 0) - (parts.lonAt || 0)) > 2000) return;
      latitude = parts.latitude;
      longitude = parts.longitude;
    }

    const fixed = parseSignalkPosition({
      navigation: {
        position: { latitude, longitude },
      },
    });
    if (!fixed) return;

    // Only include heading/speed when this update actually carries them.
    // Reading them from `myLocation` here used a stale closure (null at WS
    // setup), so every position-only update stamped heading 0 over the real
    // compass value — the triangle snapped to north and back nonstop.
    const next = {
      latitude: fixed.latitude,
      longitude: fixed.longitude,
      ...(navExtras.heading != null ? { heading: navExtras.heading } : {}),
      ...(navExtras.speed != null ? { speed: navExtras.speed } : {}),
      ...(navExtras.depth != null ? { depth: navExtras.depth } : {}),
    };
    updatePosition(next);

    if (locationPreferences?.share_location) {
      updateLocationInDatabase(next.latitude, next.longitude, navExtras.heading ?? null, navExtras.speed ?? null);
    }
  };

  // Define all functions before useEffect hooks
  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3440; // Earth radius in nautical miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const updateLocationInDatabase = async (lat, lng, heading, speed) => {
    try {
      await api.post('/api/friends/location', {
        vessel_id: vesselId || vesselIdRef.current,
        latitude: lat,
        longitude: lng,
        heading: heading,
        speed: speed
      });
    } catch (error) {
      console.error('Error updating location:', error);
    }
  };

  const checkProximity = (locations) => {
    if (!myLocation || !locationPreferences?.proximity_notifications) return;
    
    const alerts = [];
    locations.forEach(friend => {
      if (friend.latitude && friend.longitude) {
        const distance = calculateDistance(
          myLocation.latitude,
          myLocation.longitude,
          friend.latitude,
          friend.longitude
        );
        
        if (distance <= locationPreferences.proximity_radius_nm) {
          alerts.push({
            friend: friend,
            distance: distance
          });
        }
      }
    });
    
    if (alerts.length > 0 && alerts.length !== proximityAlerts.length) {
      alerts.forEach(alert => {
        toast.success(`${alert.friend.first_name} is ${alert.distance.toFixed(2)} nm away!`, {
          icon: '📍',
          duration: 5000
        });
      });
    }
    
    setProximityAlerts(alerts);
  };

  const loadFriends = async () => {
    try {
      const response = await api.get('/api/friends');
      const data = response.data;
      setFriends(Array.isArray(data) ? data : data?.friends || []);
    } catch (error) {
      console.error('Error loading friends:', error);
      setFriends([]);
    }
  };

  const loadFriendRequests = async () => {
    try {
      const response = await api.get('/api/friends/requests');
      // Handle both old format (array) and new format (object with received/sent)
      if (Array.isArray(response.data)) {
        setFriendRequests({ received: response.data, sent: [] });
      } else {
        setFriendRequests(response.data || { received: [], sent: [] });
      }
    } catch (error) {
      console.error('Error loading friend requests:', error);
    }
  };

  const cancelRequest = async (requestId) => {
    try {
      await api.delete(`/api/friends/request/${requestId}`);
      toast.success('Friend request cancelled');
      loadFriendRequests();
    } catch (error) {
      console.error('Error cancelling friend request:', error);
      toast.error('Failed to cancel friend request');
    }
  };

  const searchUsers = async () => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    try {
      const response = await api.get(`/api/friends/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data || []);
      if (response.data && response.data.length === 0) {
        // No users found - silently handle, search results will be empty
      }
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error(error.response?.data?.error || 'Failed to search users. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const sendFriendRequest = async (friendId) => {
    try {
      await api.post('/api/friends/request', { friend_id: friendId });
      toast.success('Friend request sent');
      setSearchQuery('');
      setSearchResults([]);
      loadFriendRequests();
    } catch (error) {
      console.error('Error sending friend request:', error);
      toast.error(error.response?.data?.error || 'Failed to send friend request');
    }
  };

  const acceptRequest = async (requestId) => {
    try {
      await api.post(`/api/friends/accept/${requestId}`);
      toast.success('Friend request accepted');
      loadFriendRequests();
      loadFriends();
      loadFriendLocations();
    } catch (error) {
      console.error('Error accepting request:', error);
      toast.error('Failed to accept friend request');
    }
  };

  const rejectRequest = async (requestId) => {
    try {
      await api.post(`/api/friends/reject/${requestId}`);
      toast.success('Friend request rejected');
      loadFriendRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('Failed to reject friend request');
    }
  };

  const removeFriend = async (friendId) => {
    if (!window.confirm('Are you sure you want to remove this friend?')) {
      return;
    }
    try {
      await api.delete(`/api/friends/${friendId}`);
      toast.success('Friend removed');
      loadFriends();
      loadFriendLocations();
    } catch (error) {
      console.error('Error removing friend:', error);
      toast.error('Failed to remove friend');
    }
  };

  const loadLocationPreferences = async () => {
    try {
      const response = await api.get('/api/friends/location/preferences');
      setLocationPreferences(response.data);
    } catch (error) {
      console.error('Error loading location preferences:', error);
    }
  };

  const loadFriendLocations = async () => {
    try {
      const response = await api.get('/api/friends/locations');
      setFriendLocations(response.data);
      checkProximity(response.data);
    } catch (error) {
      console.error('Error loading friend locations:', error);
    }
  };

  const startLocationTracking = async () => {
    await fetchPosition();
  };

  const updateMyLocation = async () => {
    await fetchPosition();
  };

  const connectWebSocket = () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const backend = getBackendUrl().replace(/\/$/, '');
    const wsBase = backend.replace(/^http/, 'ws');
    const wsUrl = `${wsBase}/stream?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      setTimeout(() => {
        const subscribeMessage = {
          action: 'subscribe',
          vessel_id: vesselIdRef.current || vesselId || 1,
        };
        ws.send(JSON.stringify(subscribeMessage));
      }, 100);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'delta' && message.delta) {
          if (message.delta.updates) {
            message.delta.updates.forEach(update => {
              if (update.values) {
                update.values.forEach(pathValue => {
                  const path = pathValue.path;
                  const val = pathValue.value;

                  if (path === 'navigation.position' && val && typeof val === 'object') {
                    const fixed = parseSignalkPosition({ 'navigation.position': { value: val } });
                    if (fixed) applyVesselPosition(fixed.latitude, fixed.longitude);
                    return;
                  }

                  if (path === 'navigation.position.latitude') {
                    const lat = typeof val === 'number' ? val : parseFloat(val);
                    if (Number.isFinite(lat)) applyVesselPosition(lat, null, true);
                    return;
                  }

                  if (path === 'navigation.position.longitude') {
                    const lng = typeof val === 'number' ? val : parseFloat(val);
                    if (Number.isFinite(lng)) applyVesselPosition(null, lng, true);
                    return;
                  }

                  if (
                    path.startsWith('navigation.heading') ||
                    path.startsWith('navigation.courseOverGround') ||
                    path.startsWith('navigation.attitude')
                  ) {
                    const payload =
                      path === 'navigation.attitude' && val && typeof val === 'object'
                        ? { 'navigation.attitude': { value: val, unit: pathValue.unit } }
                        : { [path]: { value: val, unit: pathValue.unit } };
                    const headingInfo = parseSignalkHeading(payload);
                    if (headingInfo) {
                      const isCog = path.startsWith('navigation.courseOverGround');
                      if (isCog && Date.now() - lastRealHeadingAtRef.current < 15000) {
                        // A compass heading arrived recently — don't let COG
                        // (a different, noisier number) fight it for the icon.
                        return;
                      }
                      setMyLocation((prev) => {
                        if (!prev) return prev;
                        // COG is GPS noise when the boat isn't moving — at the
                        // dock it points a random direction every fix, which
                        // made the vessel triangle spin in place.
                        if (isCog && (prev.speed ?? 0) < 0.5) return prev;
                        if (!isCog) lastRealHeadingAtRef.current = Date.now();
                        if (prev.heading === headingInfo.heading) return prev;
                        return { ...prev, heading: headingInfo.heading };
                      });
                    }
                    return;
                  }

                  if (path.startsWith('navigation.speed')) {
                    const speedInfo = parseSignalkSpeed({
                      [path]: { value: val, unit: pathValue.unit },
                    });
                    if (speedInfo) {
                      setMyLocation((prev) =>
                        prev ? { ...prev, speed: speedInfo.speed } : prev
                      );
                    }
                    return;
                  }

                  if (path.includes('depth') || path.includes('Depth')) {
                    const depthInfo = parseSignalkDepth({
                      [path]: { value: val, unit: pathValue.unit },
                    });
                    if (depthInfo) {
                      setMyLocation((prev) =>
                        prev ? { ...prev, depth: depthInfo.depth } : prev
                      );
                    }
                  }
                });
              }
            });
          }
        } else if (message.type === 'snapshot' && message.data) {
          const nav = parseSignalkNavigation(message.data);
          // Drop COG-derived headings while stationary — GPS course is noise
          // at the dock and makes the vessel icon point a random direction.
          const headingIsCog = (nav?.headingPath || '').includes('courseOverGround');
          const navSpeed = nav?.speed ?? 0;
          const usableHeading =
            nav?.heading != null && !(headingIsCog && navSpeed < 0.5) ? nav.heading : undefined;
          if (usableHeading != null && !headingIsCog) lastRealHeadingAtRef.current = Date.now();
          if (nav?.latitude != null && nav?.longitude != null) {
            applyVesselPosition(nav.latitude, nav.longitude, false, {
              heading: usableHeading,
              speed: nav.speed ?? undefined,
              depth: nav.depth ?? undefined,
            });
          } else if (usableHeading != null || nav?.speed != null || nav?.depth != null) {
            setMyLocation((prev) =>
              prev
                ? {
                    ...prev,
                    ...(usableHeading != null ? { heading: usableHeading } : {}),
                    ...(nav.speed != null ? { speed: nav.speed } : {}),
                    ...(nav.depth != null ? { depth: nav.depth } : {}),
                  }
                : prev
            );
          }
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    };

    ws.onclose = () => {
      setTimeout(connectWebSocket, 5000);
    };

    wsRef.current = ws;
  };

  const handleMapClick = (e) => {
    if (isPlottingCourse) {
      setCourseWaypoints([...courseWaypoints, [e.latlng.lat, e.latlng.lng]]);
    }
  };

  const clearCourse = () => {
    setCourseWaypoints([]);
    setIsPlottingCourse(false);
  };

  // useEffect hooks after all function definitions
  useEffect(() => {
    loadFriends();
    loadFriendRequests();
    loadLocationPreferences();
    loadFriendLocations();
    connectWebSocket();

    probeChartServices().then(setChartServices).catch(() => {});

    const token = localStorage.getItem('token');
    if (token) {
      api
        .get('/api/charts/config')
        .then((resp) => {
          const key = resp.data?.navionics?.key;
          if (key) setNavionicsKey(key);
        })
        .catch(() => {});
    }

    return () => {
      if (locationUpdateIntervalRef.current) {
        clearInterval(locationUpdateIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (!vesselId || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ action: 'subscribe', vessel_id: vesselId }));
  }, [vesselId]);

  useEffect(() => {
    if (searchQuery && searchQuery.length >= 2) {
      const timeoutId = setTimeout(() => {
        searchUsers();
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (locationPreferences?.share_location) {
      const interval = setInterval(() => {
        updateMyLocation();
      }, 30000); // Update every 30 seconds
      locationUpdateIntervalRef.current = interval;
      return () => clearInterval(interval);
    }
  }, [locationPreferences]);

  const savedViewport = useMemo(() => loadChartViewport(), []);

  const lookupPoiDetails = useCallback(async (poi) => {
    if (!poi?.id) return;
    setPoiDetails((prev) => {
      if (prev[poi.id] !== undefined) return prev;
      return { ...prev, [poi.id]: { loading: true } };
    });
    try {
      const params = { lat: poi.latitude, lon: poi.longitude };
      if (poi.acId) params.acId = poi.acId;
      const resp = await api.get('/api/geo/poi-details', { params });
      setPoiDetails((prev) => ({ ...prev, [poi.id]: resp.data || {} }));
    } catch {
      setPoiDetails((prev) => ({ ...prev, [poi.id]: {} }));
    }
  }, []);

  const center = useMemo(
    () => (myLocation ? [myLocation.latitude, myLocation.longitude] : null),
    [myLocation?.latitude, myLocation?.longitude]
  );
  const chartRegion = useMemo(
    () => getMarineChartRegion(myLocation?.latitude, myLocation?.longitude),
    [myLocation?.latitude, myLocation?.longitude]
  );
  const chartSourceLine = useMemo(
    () =>
      chartRegionDescription(chartRegion, {
        navionicsAvailable: Boolean(navionicsKey),
        cmapAvailable: Boolean(chartServices?.cmap),
      }),
    [chartRegion, navionicsKey, chartServices?.cmap]
  );
  const liveDepthM = myLocation?.depth;
  const mapInitialCenter = savedViewport?.center || center || [44.35, -78.22];
  const initialZoom = savedViewport?.zoom ?? CHART_ZOOM;

  // Fetch AIS vessels when map bounds change
  const fetchAisVessels = async (bounds) => {
    if (!showAisVessels || !bounds) return;

    try {
      const sw = bounds.getSouthWest?.() || bounds._southWest;
      const ne = bounds.getNorthEast?.() || bounds._northEast;
      if (!sw || !ne) return;

      const mapCenter = center || [(sw.lat + ne.lat) / 2, (sw.lng + ne.lng) / 2];
      const response = await api.get('/api/ais/vessels', {
        params: {
          minLat: sw.lat,
          minLon: sw.lng,
          maxLat: ne.lat,
          maxLon: ne.lng,
          centerLat: mapCenter[0],
          centerLon: mapCenter[1],
        },
      });
      setAisVessels(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching AIS vessels:', error);
      if (center && center[0] != null && center[1] != null) {
        try {
          const response = await api.get('/api/ais/vessels', {
            params: {
              centerLat: center[0],
              centerLon: center[1],
            },
          });
          setAisVessels(Array.isArray(response.data) ? response.data : []);
        } catch (demoError) {
          console.error('Error fetching demo AIS vessels:', demoError);
        }
      }
    }
  };

  // Fetch marine POIs (marinas, fuel docks, locks) for the visible map area
  const fetchPois = useCallback(async (bounds) => {
    if (!showPoisRef.current || !bounds) return;
    try {
      const sw = bounds.getSouthWest?.() || bounds._southWest;
      const ne = bounds.getNorthEast?.() || bounds._northEast;
      if (!sw || !ne) return;

      const response = await api.get('/api/geo/pois', {
        params: { minLat: sw.lat, minLon: sw.lng, maxLat: ne.lat, maxLon: ne.lng },
      });
      setPoisTooWide(Boolean(response.data?.tooWide));
      const next = Array.isArray(response.data?.pois) ? response.data.pois : [];
      setPois((prev) => {
        const byId = new Map(prev.map((p) => [p.id, p]));
        for (const p of next) {
          const existing = byId.get(p.id);
          if (existing) Object.assign(existing, p);
          else byId.set(p.id, p);
        }
        return Array.from(byId.values());
      });
    } catch (error) {
      console.error('Error fetching marine POIs:', error);
    }
  }, []);

  const handleBoundsChange = useCallback((bounds) => {
    lastBoundsRef.current = bounds;
    fetchAisVessels(bounds);
    fetchPois(bounds);
  }, [fetchPois]);

  // Load POIs once map + toggle are ready (bounds tracker may fire before showPois ref syncs)
  useEffect(() => {
    if (showPois && lastBoundsRef.current) {
      fetchPois(lastBoundsRef.current);
    }
  }, [showPois, fetchPois]);


  return (
    <Container>
      <Header>
        <Title>
          <MapIcon className="w-8 h-8" />
          Vessel Tracker
          <span style={{ fontSize: '0.8rem', fontWeight: 500, color: theme.colors.text.secondary, marginLeft: '0.5rem' }}>
            {locationLoading
              ? 'Acquiring GPS…'
              : myLocation
                ? `${myLocation.latitude.toFixed(5)}, ${myLocation.longitude.toFixed(5)}${locationSource ? ` (${locationSource})` : ''}`
                : 'No GPS fix — check stream'}
          </span>
        </Title>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Button
            onClick={() => {
              if (mob) {
                if (window.confirm('Clear the MOB mark?')) clearMob();
              } else if (myLocation) {
                activateMob(myLocation.latitude, myLocation.longitude);
              } else {
                toast.error('No GPS position yet — cannot mark MOB');
              }
            }}
            style={{
              background: '#dc2626',
              borderColor: '#dc2626',
              color: 'white',
              fontWeight: 800,
            }}
            title={mob ? 'Clear man-overboard mark' : 'Mark man overboard at current position'}
          >
            🆘 {mob ? 'Clear MOB' : 'MOB'}
          </Button>
          {recording ? (
            <Button
              onClick={handleStopTrip}
              disabled={tripBusy}
              style={{ background: '#dc2626', borderColor: '#dc2626', color: 'white' }}
            >
              <StopIcon className="w-5 h-5" />
              Stop &amp; Save Trip
            </Button>
          ) : (
            <Button variant="primary" onClick={handleStartTrip} disabled={tripBusy || !vesselId}>
              <PlayIcon className="w-5 h-5" />
              Start Trip
            </Button>
          )}
          <Button onClick={() => navigate('/trips')}>
            <MapIcon className="w-5 h-5" />
            Saved Trips
          </Button>
          {activeTrip && (
            <Button onClick={() => navigate(`/trips/${activeTrip.id}`)}>
              View Recording
            </Button>
          )}
          <Button onClick={handleRecenterVessel} disabled={!myLocation}>
            <MapIcon className="w-5 h-5" />
            Center on Vessel
          </Button>
          <Button onClick={() => setIsPlottingCourse(!isPlottingCourse)}>
            <PlusIcon className="w-5 h-5" />
            {isPlottingCourse ? 'Cancel Plot' : 'Plot Course'}
          </Button>
          {courseWaypoints.length > 0 && (
            <Button onClick={clearCourse}>
              <XMarkIcon className="w-5 h-5" />
              Clear Course
            </Button>
          )}
          <Button onClick={loadFriendLocations}>
            <ArrowPathIcon className="w-5 h-5" />
            Refresh
          </Button>
          <Button onClick={() => setShowFriendsPanel(!showFriendsPanel)}>
            <UserGroupIcon className="w-5 h-5" />
            {showFriendsPanel ? 'Hide' : 'Manage'} Friends
          </Button>
        </div>
      </Header>

      <WeatherAlertBanner position={myLocation} />

      <MapWrapper>
        <PlaceSearch
          vesselPosition={myLocation}
          onResultSelected={(r) => {
            setFollowVessel(false);
            localStorage.setItem('follow_vessel', 'false');
            setSearchTarget(r);
          }}
        />
        <MobPanel mob={mob} myLocation={myLocation} onClear={clearMob} />
        {proximityAlerts.length > 0 && (
          <NotificationBadge>
            <BellIcon className="w-5 h-5" />
            {proximityAlerts.length} friend{proximityAlerts.length > 1 ? 's' : ''} nearby!
          </NotificationBadge>
        )}

        {/* Map Menu Button */}
        <MapRecenterButton
          type="button"
          onClick={handleRecenterVessel}
          disabled={!myLocation}
          title="Center map on your vessel"
          aria-label="Center map on your vessel"
        >
          <CompassRecenterIcon />
        </MapRecenterButton>

        <MapMenuButton onClick={() => setShowMapMenu(!showMapMenu)} title="Map Options">
          <Bars3Icon className="w-6 h-6" />
        </MapMenuButton>

        {recording && (
          <RecordingBadge>
            <span className="dot" />
            Recording: {activeTrip?.trip_name || 'Trip in progress'}
          </RecordingBadge>
        )}

        {!locationLoading && !myLocation && (
          <GpsHint>
            No GPS on map yet — checking vessel stream and saved SignalK data…
          </GpsHint>
        )}

        {showPois && poisTooWide && (
          <GpsHint style={{ bottom: '8rem' }}>
            Zoom in to load marinas, fuel docks &amp; locks
          </GpsHint>
        )}

        {mapStyle === 'marine' && (
          <DepthHud
            $shallow={Number.isFinite(liveDepthM) && liveDepthM < 3}
            $missing={!Number.isFinite(liveDepthM)}
          >
            <span className="label">Depth (live)</span>
            <span className="value">
              {Number.isFinite(liveDepthM) ? `${liveDepthM.toFixed(1)} m` : '—'}
            </span>
            {!Number.isFinite(liveDepthM) && (
              <DepthHudNote>No transducer data on stream yet</DepthHudNote>
            )}
          </DepthHud>
        )}

        {mapStyle === 'marine' && chartRegion === 'canada_inland' && !navionicsKey && !chartServices?.cmap && (
          <ChartSourceHint title={chartSourceLine}>
            {chartSourceLine}
            {' '}
            <a
              href={navionicsWebChartUrl(myLocation?.latitude, myLocation?.longitude)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: theme.colors.primary.main, pointerEvents: 'auto' }}
            >
              Open Navionics charts ↗
            </a>
          </ChartSourceHint>
        )}

        {mapStyle === 'marine' && !(chartRegion === 'canada_inland' && !navionicsKey && !chartServices?.cmap) && (
          <ChartSourceHint title={chartSourceLine}>{chartSourceLine}</ChartSourceHint>
        )}

        {/* Map Menu */}
        {showMapMenu && (
          <MapMenu>
            <MenuItem>
              <MenuLabel>
                <MenuCheckbox
                  type="checkbox"
                  checked={showFriendsOnMap}
                  onChange={(e) => setShowFriendsOnMap(e.target.checked)}
                />
                Show Friends on Map
              </MenuLabel>
            </MenuItem>
            <MenuItem>
              <MenuLabel>
                <MenuCheckbox
                  type="checkbox"
                  checked={showAisVessels}
                  onChange={(e) => setShowAisVessels(e.target.checked)}
                />
                Show AIS Vessels
              </MenuLabel>
            </MenuItem>
            <MenuItem>
              <MenuLabel>
                <MenuCheckbox
                  type="checkbox"
                  checked={showPois}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setShowPois(on);
                    localStorage.setItem('show_marine_pois', on ? 'true' : 'false');
                    showPoisRef.current = on;
                    if (on && lastBoundsRef.current) {
                      fetchPois(lastBoundsRef.current);
                    } else if (!on) {
                      setPois([]);
                    }
                  }}
                />
                Marinas, Fuel &amp; Locks
              </MenuLabel>
            </MenuItem>
            <MenuItem>
              <MenuLabel>
                <MenuCheckbox
                  type="radio"
                  name="map_style"
                  checked={mapStyle === 'marine'}
                  onChange={() => {
                    setMapStyle('marine');
                    localStorage.setItem('map_base_layer', 'marine');
                  }}
                />
                <strong>Marine charts</strong> (depth &amp; nav aids)
              </MenuLabel>
            </MenuItem>
            <MenuItem>
              <MenuLabel>
                <MenuCheckbox
                  type="radio"
                  name="map_style"
                  checked={mapStyle === 'street'}
                  onChange={() => {
                    setMapStyle('street');
                    localStorage.setItem('map_base_layer', 'street');
                  }}
                />
                Street map
              </MenuLabel>
            </MenuItem>
            <MenuItem>
              <MenuLabel>
                <MenuCheckbox
                  type="radio"
                  name="map_style"
                  checked={mapStyle === 'satellite'}
                  onChange={() => {
                    setMapStyle('satellite');
                    localStorage.setItem('map_base_layer', 'satellite');
                  }}
                />
                Satellite
              </MenuLabel>
            </MenuItem>
            {mapStyle === 'marine' && (
              <>
                <MenuItem>
                  <MenuLabel>
                    <MenuCheckbox
                      type="checkbox"
                      checked={showChsBathymetry}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setShowChsBathymetry(on);
                        localStorage.setItem('show_chs_bathymetry', on ? 'true' : 'false');
                      }}
                    />
                    CHS bathymetry (NONNA)
                  </MenuLabel>
                </MenuItem>
                <MenuItem>
                  <MenuLabel>
                    <MenuCheckbox
                      type="checkbox"
                      checked={showDepthLayers}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setShowDepthLayers(on);
                        localStorage.setItem('show_depth_layers', on ? 'true' : 'false');
                      }}
                    />
                    Depth contours (OpenSeaMap)
                  </MenuLabel>
                </MenuItem>
                {chartServices?.vectorCharts && chartRegion === 'us' && (
                  <MenuItem>
                    <MenuLabel>
                      <MenuCheckbox
                        type="checkbox"
                        checked={useVectorCharts}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setUseVectorCharts(on);
                          localStorage.setItem('use_vector_charts', on ? 'true' : 'false');
                        }}
                      />
                      VectorCharts (US ENC)
                    </MenuLabel>
                  </MenuItem>
                )}
                <MenuItem style={{ fontSize: '0.72rem', color: theme.colors.text.secondary, lineHeight: 1.35 }}>
                  {chartRegionLabel(chartRegion)}
                  {chartServices?.cmap ? ' · C-MAP available' : ''}
                  {navionicsKey ? ' · Navionics' : ''}
                </MenuItem>
              </>
            )}
            <MenuItem>
              <MenuLabel>
                <MenuCheckbox
                  type="checkbox"
                  checked={followVessel}
                  onChange={(e) => {
                    setFollowVessel(e.target.checked);
                    localStorage.setItem('follow_vessel', e.target.checked.toString());
                  }}
                />
                <strong>Follow Vessel</strong> (keep map on GPS) {followVessel && '✓'}
              </MenuLabel>
            </MenuItem>
            <MenuItem>
              <Button
                variant={null}
                style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem', width: '100%', textAlign: 'center' }}
                onClick={() => {
                  handleRecenterVessel();
                  fetchPosition();
                  toast.success('Centered on vessel');
                }}
              >
                Center on vessel
              </Button>
            </MenuItem>
          </MapMenu>
        )}

        <MapContainer
          center={mapInitialCenter}
          zoom={initialZoom}
          minZoom={2}
          maxZoom={CHART_MAX_ZOOM}
          style={{ height: '100%', width: '100%' }}
          dragging={true}
          scrollWheelZoom={true}
          doubleClickZoom={true}
          zoomControl={true}
          touchZoom={true}
          boxZoom={true}
          keyboard={true}
          preferCanvas={false}
        >
          <MapInvalidateSize />
          <MapDraggingEnabler />
          <MapZoomLimits />
          <ChartViewportPersistence />
          {mapStyle === 'marine' ? (
            <>
              <IntegratedMarineChartLayer
                latitude={myLocation?.latitude}
                longitude={myLocation?.longitude}
                navionicsKey={navionicsKey}
                useVectorCharts={useVectorCharts}
                vectorChartsAvailable={Boolean(chartServices?.vectorCharts)}
                cmapAvailable={Boolean(chartServices?.cmap)}
              />
              <MarineDepthOverlays
                latitude={myLocation?.latitude}
                longitude={myLocation?.longitude}
                showChsBathymetry={showChsBathymetry}
                showOpenSeaMapDepth={showDepthLayers}
              />
            </>
          ) : (
            <TileLayer
              key={mapStyle}
              attribution={mapStyle === 'satellite' ? SATELLITE_MAP_ATTRIBUTION : STREET_MAP_ATTRIBUTION}
              url={mapStyle === 'satellite' ? SATELLITE_MAP_URL : STREET_MAP_URL}
              {...(STREET_MAP_SUBDOMAINS && mapStyle !== 'satellite'
                ? { subdomains: STREET_MAP_SUBDOMAINS }
                : {})}
              maxZoom={CHART_MAX_ZOOM}
              minZoom={2}
            />
          )}
          <VesselMapTracker
            position={center}
            zoom={CHART_ZOOM}
            follow={followVessel}
            recenterToken={recenterToken}
            speedKnots={myLocation?.speed ?? 0}
            skipInitialCenter={Boolean(savedViewport)}
          />
          <MapBoundsTracker onBoundsChange={handleBoundsChange} />
          <SearchFlyTo target={searchTarget} />
          {isPlottingCourse && <MapClickHandler onMapClick={handleMapClick} />}

          <MobMarker mob={mob} />

          {showPois && (
            <MarinePoiLayer
              pois={pois}
              vesselPosition={myLocation}
              poiDetails={poiDetails}
              onLookupDetails={lookupPoiDetails}
            />
          )}

          {searchTarget && (
            <SearchPlaceMarker
              target={searchTarget}
              vesselPosition={myLocation}
              onClose={() => setSearchTarget(null)}
            />
          )}

          {trackPoints.length > 1 && (
            <Polyline
              positions={trackPoints}
              pathOptions={{ color: '#2563eb', weight: 4, opacity: 0.85 }}
            />
          )}

          {/* My vessel marker */}
          {myLocation && (
            <Marker
              position={[myLocation.latitude, myLocation.longitude]}
              icon={createVesselIcon('#3b82f6', myLocation.heading ?? 0)}
              zIndexOffset={1000}
            >
              <Popup>
                <div>
                  <strong>My Vessel</strong>
                  <br />
                  Position: {myLocation.latitude.toFixed(6)}, {myLocation.longitude.toFixed(6)}
                  <br />
                  Heading: {(myLocation.heading ?? 0).toFixed(1)}°
                  <br />
                  Speed: {(myLocation.speed ?? 0).toFixed(1)} knots
                  {Number.isFinite(myLocation.depth) && (
                    <>
                      <br />
                      Depth: {myLocation.depth.toFixed(1)} m
                    </>
                  )}
                </div>
              </Popup>
            </Marker>
          )}

          {/* Friend markers */}
          {showFriendsOnMap && friendLocations.map((friend) => (
            <Marker
              key={friend.user_id}
              position={[friend.latitude, friend.longitude]}
              icon={createFriendIcon('#10b981')}
            >
              <Popup>
                <div>
                  <strong>{friend.first_name} {friend.last_name}</strong>
                  {friend.vessel_name && <><br />Vessel: {friend.vessel_name}</>}
                  <br />
                  Position: {friend.latitude.toFixed(6)}, {friend.longitude.toFixed(6)}
                  {friend.heading && <><br />Heading: {friend.heading.toFixed(1)}°</>}
                  {friend.speed && <><br />Speed: {friend.speed.toFixed(1)} knots</>}
                  {myLocation && (
                    <>
                      <br />
                      Distance: {calculateDistance(
                        myLocation.latitude,
                        myLocation.longitude,
                        friend.latitude,
                        friend.longitude
                      ).toFixed(2)} nm
                    </>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Proximity circles */}
          {showFriendsOnMap && locationPreferences?.proximity_notifications && myLocation && locationPreferences?.proximity_radius_nm && (
            <Circle
              center={[myLocation.latitude, myLocation.longitude]}
              radius={locationPreferences.proximity_radius_nm * 1852}
              pathOptions={{ color: '#f59e0b', fillColor: '#f59e0b', fillOpacity: 0.1 }}
            />
          )}

          {/* Course waypoints */}
          {courseWaypoints.map((waypoint, index) => (
            <Marker
              key={index}
              position={waypoint}
              icon={createVesselIcon(index === 0 ? '#10b981' : '#ef4444', 0)}
            >
              <Popup>Waypoint {index + 1}</Popup>
            </Marker>
          ))}

          {/* AIS Vessels */}
          {showAisVessels && aisVessels.map((vessel) => (
            <Marker
              key={vessel.mmsi}
              position={[vessel.latitude, vessel.longitude]}
              icon={createAisVesselIcon('#ef4444')}
            >
              <Popup>
                <div>
                  <strong>{vessel.name || 'Unknown Vessel'}</strong>
                  <br />
                  MMSI: {vessel.mmsi}
                  <br />
                  Type: {vessel.type || 'Unknown'}
                  <br />
                  Position: {vessel.latitude.toFixed(6)}, {vessel.longitude.toFixed(6)}
                  {vessel.speed !== undefined && <><br />Speed: {vessel.speed.toFixed(1)} knots</>}
                  {vessel.course !== undefined && <><br />Course: {vessel.course.toFixed(1)}°</>}
                  {vessel.destination && <><br />Destination: {vessel.destination}</>}
                  {vessel.length && <><br />Length: {vessel.length.toFixed(1)}m</>}
                  {vessel.width && <><br />Width: {vessel.width.toFixed(1)}m</>}
                  {vessel.draught && <><br />Draught: {vessel.draught.toFixed(1)}m</>}
                  {myLocation && (
                    <>
                      <br />
                      Distance: {calculateDistance(
                        myLocation.latitude,
                        myLocation.longitude,
                        vessel.latitude,
                        vessel.longitude
                      ).toFixed(2)} nm
                    </>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {showFriendsPanel ? (
          <FriendsPanel>
            <SidebarHeader>
              <SidebarTitle>Manage Friends</SidebarTitle>
              <Button onClick={() => setShowFriendsPanel(false)} style={{ padding: '0.5rem' }}>
                <XMarkIcon className="w-4 h-4" />
              </Button>
            </SidebarHeader>
            
            {/* Show Friends on Map Toggle */}
            <div style={{ 
              padding: '1rem', 
              borderBottom: `1px solid ${theme.colors.border.secondary}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                cursor: 'pointer',
                fontSize: theme.fontSizes.sm,
                color: theme.colors.text.primary
              }}>
                <input
                  type="checkbox"
                  checked={showFriendsOnMap}
                  onChange={(e) => setShowFriendsOnMap(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                Show friends on map
              </label>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', marginBottom: '1rem', borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
              <TabButton 
                $active={friendsTab === 'friends'} 
                onClick={() => setFriendsTab('friends')}
                style={{ cursor: 'pointer' }}
              >
                Friends
              </TabButton>
              <TabButton 
                $active={friendsTab === 'requests'} 
                onClick={() => setFriendsTab('requests')}
                style={{ cursor: 'pointer' }}
              >
                Requests {friendRequests.received && friendRequests.received.length > 0 && `(${friendRequests.received.length})`}
              </TabButton>
            </div>

            {/* Tab Content */}
            {friendsTab === 'friends' ? (
              <>
                {/* Search for new friends */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ position: 'relative' }}>
                    <SearchInput
                      type="text"
                      placeholder="Search for users (min 2 characters)..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      disabled={isSearching}
                    />
                    {isSearching && (
                      <div style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)' }}>
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      </div>
                    )}
                  </div>
                  {searchQuery.length > 0 && searchQuery.length < 2 && (
                    <div style={{ fontSize: theme.fontSizes.xs, color: theme.colors.text.secondary, marginTop: '0.25rem' }}>
                      Type at least 2 characters to search
                    </div>
                  )}
                  {searchResults.length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      {searchResults.map((user) => (
                        <div key={user.id} style={{ display: 'flex', alignItems: 'center', padding: '0.5rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md, marginBottom: '0.5rem' }}>
                          <UserAvatar>{getInitials(user.first_name, user.last_name)}</UserAvatar>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: theme.fontWeights.semibold }}>{user.first_name} {user.last_name}</div>
                            <div style={{ fontSize: theme.fontSizes.xs, color: theme.colors.text.secondary }}>{user.email}</div>
                          </div>
                          <ActionButton onClick={() => sendFriendRequest(user.id)}>
                            <UserPlusIcon className="w-4 h-4" />
                          </ActionButton>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Friends List */}
                {friends.length > 0 ? (
                  <div>
                    <h4 style={{ fontSize: theme.fontSizes.sm, fontWeight: theme.fontWeights.semibold, marginBottom: '0.5rem' }}>My Friends</h4>
                    {friends.map((friend) => (
                      <div key={friend.id} style={{ display: 'flex', alignItems: 'center', padding: '0.5rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md, marginBottom: '0.5rem' }}>
                        <UserAvatar>{getInitials(friend.first_name, friend.last_name)}</UserAvatar>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: theme.fontWeights.semibold }}>{friend.first_name} {friend.last_name}</div>
                          <div style={{ fontSize: theme.fontSizes.xs, color: theme.colors.text.secondary }}>{friend.email}</div>
                        </div>
                        <ActionButton 
                          variant="danger" 
                          onClick={() => removeFriend(friend.id)}
                          title="Remove friend"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </ActionButton>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
                    <p>No friends yet. Search above to add friends!</p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Received Friend Requests */}
                {friendRequests.received && friendRequests.received.length > 0 ? (
                  <div style={{ marginBottom: '1rem' }}>
                    <h4 style={{ fontSize: theme.fontSizes.sm, fontWeight: theme.fontWeights.semibold, marginBottom: '0.5rem' }}>Received Requests</h4>
                    {friendRequests.received.map((request) => (
                      <div key={request.id} style={{ display: 'flex', alignItems: 'center', padding: '0.5rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md, marginBottom: '0.5rem' }}>
                        <UserAvatar>{getInitials(request.first_name, request.last_name)}</UserAvatar>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: theme.fontWeights.semibold }}>{request.first_name} {request.last_name}</div>
                          <div style={{ fontSize: theme.fontSizes.xs, color: theme.colors.text.secondary }}>{request.email || 'No email'}</div>
                        </div>
                        <ActionButton 
                          variant="success" 
                          onClick={async () => {
                            console.log('Accepting friend request:', request.id);
                            await acceptRequest(request.id);
                          }}
                          title="Accept request"
                        >
                          <CheckIcon className="w-4 h-4" />
                        </ActionButton>
                        <ActionButton 
                          variant="danger" 
                          onClick={async () => {
                            console.log('Rejecting friend request:', request.id);
                            await rejectRequest(request.id);
                          }}
                          title="Reject request"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </ActionButton>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '1rem', color: theme.colors.text.secondary }}>
                    <p>No received requests</p>
                  </div>
                )}

                {/* Sent Friend Requests */}
                {friendRequests.sent && friendRequests.sent.length > 0 && (
                  <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                    <h4 style={{ fontSize: theme.fontSizes.sm, fontWeight: theme.fontWeights.semibold, marginBottom: '0.5rem' }}>Sent Requests</h4>
                    {friendRequests.sent.map((request) => (
                      <div key={request.id} style={{ display: 'flex', alignItems: 'center', padding: '0.5rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md, marginBottom: '0.5rem' }}>
                        <UserAvatar>{getInitials(request.first_name, request.last_name)}</UserAvatar>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: theme.fontWeights.semibold }}>{request.first_name} {request.last_name}</div>
                          <div style={{ fontSize: theme.fontSizes.xs, color: theme.colors.text.secondary }}>Pending...</div>
                        </div>
                        <ActionButton 
                          variant="danger" 
                          onClick={async () => {
                            console.log('Cancelling friend request:', request.id);
                            await cancelRequest(request.id);
                          }}
                          title="Cancel request"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </ActionButton>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Friends List */}
            <div>
              <h4 style={{ fontSize: theme.fontSizes.sm, fontWeight: theme.fontWeights.semibold, marginBottom: '0.5rem' }}>My Friends</h4>
              {friends.length === 0 ? (
                <div style={{ color: theme.colors.text.secondary, textAlign: 'center', padding: '2rem' }}>
                  No friends yet. Search above to add friends!
                </div>
              ) : (
                friends.map((friend) => {
                  const location = friendLocations.find(loc => loc.user_id === friend.friend_user_id);
                  return (
                    <FriendListItem
                      key={friend.friend_user_id}
                      $active={selectedFriend === friend.friend_user_id}
                      onClick={() => {
                        if (location) {
                          setSelectedFriend(friend.friend_user_id);
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <UserAvatar style={{ width: '24px', height: '24px', fontSize: theme.fontSizes.xs, marginRight: '0.5rem' }}>
                            {getInitials(friend.first_name, friend.last_name)}
                          </UserAvatar>
                          <div>
                            <FriendName>
                              {friend.first_name} {friend.last_name}
                            </FriendName>
                            <FriendInfo>
                              {friend.vessel_name && <div>Vessel: {friend.vessel_name}</div>}
                              {location ? (
                                <>
                                  <StatusBadge $active={true}>📍 On Map</StatusBadge>
                                  {myLocation && (
                                    <div style={{ marginTop: '0.5rem', fontSize: theme.fontSizes.xs }}>
                                      {calculateDistance(
                                        myLocation.latitude,
                                        myLocation.longitude,
                                        location.latitude,
                                        location.longitude
                                      ).toFixed(2)} nm away
                                    </div>
                                  )}
                                </>
                              ) : (
                                <StatusBadge $active={false}>Location not shared</StatusBadge>
                              )}
                            </FriendInfo>
                          </div>
                        </div>
                        <ActionButton variant="danger" onClick={(e) => { e.stopPropagation(); removeFriend(friend.friend_user_id); }}>
                          <TrashIcon className="w-4 h-4" />
                        </ActionButton>
                      </div>
                    </FriendListItem>
                  );
                })
              )}
            </div>
          </FriendsPanel>
        ) : null}
      </MapWrapper>
    </Container>
  );
};

export default ChartPlotter;

